import { ReactNode, RefObject, useEffect, useState } from 'react';

import { useWsStatus } from '@paradigm/features/src/unified-rfqs/contexts/use-ws-status';
import FillScrollable from '#/components/FillScrollable';

import { FailedResp } from '@paradigm/api/src/fetch-api';
import {
  ListReq,
  MergedSourcesFetcher,
  Status,
} from '@paradigm/features/src/unified-rfqs/services/merged-sources-fetcher';
import { BaseEntity } from '@paradigm/features/src/unified-rfqs/store/entity';
import { useRfqFiltersValue } from '@paradigm/features/src/unified-rfqs/ui/rfq-filters/filter-provider';
import BaseBottomLoader from './BaseBottomLoader';

interface Props<T extends MergedSourcesFetcher<ListReq, BaseEntity>> {
  status: Status;
  fetcher: T;
  scrollableRef: RefObject<HTMLDivElement>;
  isEmpty: boolean;
  canFetchMore: boolean;
  entityRowsList: ReactNode;
  headerRow: ReactNode;
  dataTestId: string;
  emptyListMessage: string;
}

export default function BaseTable<
  T extends MergedSourcesFetcher<ListReq, BaseEntity>,
>(props: Props<T>) {
  const [fetchError, setFetchError] = useState('');
  const { state: filterState } = useRfqFiltersValue();
  const {
    fetcher,
    isEmpty,
    status,
    canFetchMore,
    emptyListMessage,
    entityRowsList,
    dataTestId,
    headerRow,
    scrollableRef,
  } = props;
  const { error: wsError, subscribed, subscribing, connected } = useWsStatus();
  useEffect(() => {
    if (!subscribed) return;
    setFetchError('');
    fetcher.fetchLatest(
      {
        productCodes: filterState.productCodes,
        strategyCodes: filterState.strategyCodes,
      },
      (resp: FailedResp) => {
        setFetchError(resp.message);
      },
    );
  }, [
    fetcher,
    subscribed,
    filterState.productCodes,
    filterState.strategyCodes,
  ]);

  const error = fetchError.length > 0 ? fetchError : wsError;
  const hasError = error.length > 0;

  const content: ReactNode = hasError ? (
    <pre css="color: red; margin-top: 1rem; text-align: center">{error}</pre>
  ) : connected && !subscribing && status === 'idle' && isEmpty ? (
    <p css="opacity: 0.7; margin-top: 1rem; text-align: center">
      {emptyListMessage}
    </p>
  ) : (
    entityRowsList
  );
  return (
    <FillScrollable ref={scrollableRef} data-testid={dataTestId}>
      {headerRow}
      {content}
      <BaseBottomLoader
        scrollableRef={scrollableRef}
        fetcher={fetcher}
        canFetchMore={canFetchMore}
        status={status}
      />
    </FillScrollable>
  );
}
